<template>
  <div v-loading="loading">
    <div class="fc-hero">
      <div>
        <div class="fc-hero__badge">Moliya kalendari</div>
        <h2 class="fc-hero__title">Ma'lumot turlari</h2>
        <p class="fc-hero__sub">
          Portaldagi jadvalning qatorlari: «Budjet balansi», «Mahalliy budjetlar» va hokazo.
        </p>
      </div>
      <div class="fc-hero__actions">
        <el-button plain @click="fetchList">Yangilash</el-button>
        <el-button type="primary" @click="openCreate">+ Qator qo'shish</el-button>
      </div>
    </div>

    <MfEmpty
        v-if="!loading && !rows.length"
        title="Ma'lumot turlari topilmadi"
        description="Yangi qator yaratish uchun yuqoridagi tugmani bosing"
    />

    <div v-else class="fc-table-wrap">
      <el-table :data="rows" :row-class-name="rowClass">
        <el-table-column label="Qator nomi" min-width="280">
          <template #default="scope">
            <div class="fc-cell-title">
              <span class="fc-cell-title__main">{{ title(scope.row) }}</span>
              <span class="fc-langs">
                <span
                    v-for="lt in langTabs"
                    :key="lt.code"
                    class="fc-lang-chip"
                    :class="{'is-filled': !!scope.row.title?.[lt.code]}"
                    :title="langLabels[lt.code]"
                >{{ lt.tabLabel }}</span>
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Havola" min-width="220">
          <template #default="scope">
            <a
                v-if="scope.row.link_url"
                :href="scope.row.link_url"
                target="_blank"
                rel="noopener"
                class="fc-link"
            >{{ scope.row.link_url }}</a>
            <span v-else class="fc-cell-title__sub">—</span>
          </template>
        </el-table-column>

        <el-table-column label="Tartib" width="90" align="center" prop="order_number"/>

        <el-table-column label="Holati" width="110" align="center">
          <template #default="scope">
            <el-switch
                :model-value="scope.row.is_active !== false"
                :loading="togglingId === scope.row.id"
                style="--el-switch-on-color: #16A34A"
                @change="onToggleActive(scope.row)"
            />
          </template>
        </el-table-column>

        <el-table-column fixed="right" label="Amallar" width="120" align="center">
          <template #default="scope">
            <el-tooltip content="Tahrirlash" placement="top" :hide-after="0">
              <el-button size="small" type="primary" :icon="Edit" @click="openEdit(scope.row)"/>
            </el-tooltip>
            <el-tooltip content="O'chirish" placement="top" :hide-after="0">
              <el-button
                  size="small"
                  type="danger"
                  :icon="Delete"
                  :loading="deletingId === scope.row.id"
                  @click="onDelete(scope.row)"
              />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <DatasetDialog
        v-model="dialogVisible"
        :dataset="editing"
        :next-order="nextOrder"
        @saved="fetchList"
    />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import { Delete, Edit } from "@element-plus/icons-vue";
import MfEmpty from "@/components/core/MfEmpty.vue";
import DatasetDialog from "./DatasetDialog.vue";
import api, { LANG_TABS, LANG_LABELS, displayLangText, apiErrorMessage } from "./financeCalendarApi";

export default {
  name: "DatasetList",
  components: { MfEmpty, DatasetDialog },
  data() {
    return {
      Edit,
      Delete,
      loading: false,
      rows: [],
      dialogVisible: false,
      editing: null,
      togglingId: null,
      deletingId: null,
      langTabs: LANG_TABS,
      langLabels: LANG_LABELS,
    };
  },
  computed: {
    // Yangi qatorga tartib raqami avtomatik: mavjudlaridagi eng kattasi + 1.
    nextOrder() {
      const orders = this.rows
          .map((row) => Number(row.order_number))
          .filter((value) => Number.isFinite(value));
      return orders.length ? Math.max(...orders) + 1 : 1;
    },
  },
  created() {
    this.fetchList();
  },
  methods: {
    title(row) {
      return displayLangText(row.title) || "—";
    },
    rowClass({ row }) {
      return row.is_active === false ? "fc-row--inactive" : "";
    },
    fetchList() {
      this.loading = true;
      api
          .datasets()
          .then((rows) => {
            this.rows = rows;
          })
          .catch((error) => {
            ElNotification.error({ title: "Xatolik", message: apiErrorMessage(error) });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    openCreate() {
      this.editing = null;
      this.dialogVisible = true;
    },
    openEdit(row) {
      this.editing = row;
      this.dialogVisible = true;
    },
    // PUT to'liq almashtirish — avval to'liq yozuvni olamiz (JAVOBLAR.md A3).
    onToggleActive(row) {
      this.togglingId = row.id;
      api
          .dataset(row.id)
          .then((data) =>
              api.updateDataset(row.id, {
                title: data.title,
                link_url: data.link_url,
                order_number: data.order_number,
                is_active: !(data.is_active !== false),
              })
          )
          .then(() => {
            ElMessage({ type: "success", message: "Holati yangilandi" });
          })
          .catch((error) => {
            ElNotification.error({ title: "Xatolik", message: apiErrorMessage(error) });
          })
          .finally(() => {
            this.togglingId = null;
            this.fetchList();
          });
    },
    // Qator o'chirilganda uning hamma hodisalari ham soft delete bo'ladi
    // (CONSOLE.md §5), shuning uchun confirm'da aniq sonini ko'rsatamiz.
    onDelete(row) {
      this.deletingId = row.id;
      api
          .events({ dataset_id: row.id })
          .catch(() => [])
          .then((events) => {
            const count = events.length;
            const message = count
                ? `«${this.title(row)}» qatori va unga tegishli ${count} ta hodisa o'chiriladi. Admin paneldan qaytarib bo'lmaydi. Davom etasizmi?`
                : `«${this.title(row)}» qatorini o'chirasizmi? Admin paneldan qaytarib bo'lmaydi.`;
            return ElMessageBox.confirm(message, "Qatorni o'chirish", {
              confirmButtonText: "Ha, o'chirish",
              cancelButtonText: "Bekor qilish",
              type: "warning",
            });
          })
          .then(() =>
              api.deleteDataset(row.id).then(() => {
                ElMessage({ type: "success", message: "Qator o'chirildi" });
                this.fetchList();
              })
          )
          .catch((error) => {
            // ElMessageBox bekor qilinsa ham shu yerga tushadi — u holda
            // `response` bo'lmaydi va hech narsa ko'rsatmaymiz.
            if (error?.response) {
              ElNotification.error({ title: "O'chirib bo'lmadi", message: apiErrorMessage(error) });
            }
          })
          .finally(() => {
            this.deletingId = null;
          });
    },
  },
};
</script>

<style scoped lang="scss">
@use "./finance-calendar.scss";

.fc-link {
  color: #409eff;
  word-break: break-all;
}
</style>
